/**
 * BayesianGatePill — global, read-only AppShell status pill.
 *
 * Shows the current Bayesian-streak gate state on every authed
 * page so the operator has at-a-glance visibility from anywhere
 * in ApexFlow. Click → navigates to /bayesian-gate-tracker.
 *
 * Visual contract:
 *   • Border color reflects today-state, not just gate-state:
 *       - Gate OPEN          → bright green (locked-in success)
 *       - Gate CLOSED, today PASS → green (trend is right)
 *       - Gate CLOSED, today FAIL → red    (trend is wrong)
 *       - Gate CLOSED, today —    → amber  (insufficient evidence)
 *   • A pulsing dot indicates "today is passing" while the gate
 *     remains closed — early signal of trajectory.
 *
 * Milestone notifications:
 *   • When the consecutive-positive streak crosses 7 or 14, fire
 *     a one-shot browser notification + sonner toast. State is
 *     persisted in localStorage so it never fires twice for the
 *     same milestone reach.
 *
 * Hard constraints:
 *   • READ-ONLY (consumes existing /intel/streak-driver/streak
 *     endpoint — no new backend writes).
 *   • Visible only to admin users (the endpoint is admin-only).
 *   • Hidden on /login and /trust to avoid pre-auth noise.
 *   • Mobile-safe — collapses on narrow viewports (vpw < 600).
 *   • Never flips safety flags. Never enables enforcement.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, auth } from '../../lib/api';
import { ShieldCheck, Lock } from 'lucide-react';
import useViewportWidth from '@/hooks/useViewportWidth';

const C_GREEN = '#16a34a';
const C_AMBER = '#f59e0b';
const C_RED = '#dc2626';
const C_MUTED = '#71717a';
const BG_ELEV = 'rgba(20,23,28,0.94)';
const MONO = 'JetBrains Mono, ui-monospace, monospace';

const POLL_MS = 60_000;
const MILESTONES = [7, 14];
const MILESTONE_KEY = 'apex_bgp_milestones_v1';

const HIDDEN_PATHS = new Set(['/login', '/trust',
  '/auth/callback']);


function readMilestones() {
  try {
    return JSON.parse(localStorage.getItem(MILESTONE_KEY)
      || '{}') || {};
  } catch {
    return {};
  }
}
function writeMilestones(state) {
  try {
    localStorage.setItem(MILESTONE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}


function fireMilestoneNotification(milestone, threshold) {
  const title = milestone >= threshold
    ? `Bayesian gate threshold reached: ${milestone}/${threshold}`
    : `Bayesian streak milestone: ${milestone}/${threshold}`;
  const body = milestone >= threshold
    ? 'Gate is now OPEN — operator review required before any'
      + ' enforcement decision.'
    : 'Halfway-there milestone. Trend is holding. Operator'
      + ' approval still required for any flag change.';
  // Toast for in-app visibility
  try {
    if (milestone >= threshold) {
      toast.success(title, {
        description: body, duration: 12_000,
      });
    } else {
      toast.info(title, {
        description: body, duration: 10_000,
      });
    }
  } catch { /* ignore */ }
  // Native browser notification (best-effort)
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  } catch { /* ignore */ }
}


export default function BayesianGatePill() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const vpw = useViewportWidth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  // Track if we've already requested notification permission so
  // we don't pester the user repeatedly.
  const askedNotifRef = useRef(false);

  const user = auth.currentUser();
  const isAdmin = user?.role === 'admin';
  const isAuthed = auth.isAuthed();

  const load = useCallback(async () => {
    if (!isAdmin || !isAuthed) return;
    try {
      const r = await api.get('/intel/streak-driver/streak');
      // Adapt System A → minimum shape the pill needs
      const a = r.data || {};
      const target = a.target_streak || 14;
      const cur = a.current_streak ?? 0;
      const latest = a.latest || {};
      const last30 = a.last_30d || [];
      // rows[0] is "today" — pick the most recent day from last_30d
      const sortedDesc = [...last30].sort(
        (x, y) => (y.day || '').localeCompare(x.day || ''));
      const todayRow = sortedDesc[0] || latest;
      const todayPasses = todayRow?.is_positive === true
        ? true
        : todayRow?.is_positive === false
          ? false
          : null;
      setData({
        current_consecutive_positive: cur,
        gate_threshold_days: target,
        enforcement_gate_open: cur >= target,
        evidence_inception: a.evidence_inception || null,
        rows: [{ gate_passes: todayPasses }],
      });
      setErr(false);
    } catch (e) {
      const code = e?.response?.status;
      if (code !== 401 && code !== 403) {
        setErr(true);
      }
      setData(null);
    }
  }, [isAdmin, isAuthed]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { load(); }, [pathname, load]);

  // Best-effort: request notification permission once after the
  // pill first renders for an admin. Browsers gate this behind a
  // user-gesture in many environments; we silently no-op if so.
  useEffect(() => {
    if (!isAdmin || askedNotifRef.current) return;
    askedNotifRef.current = true;
    try {
      if (typeof Notification !== 'undefined'
          && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch { /* ignore */ }
  }, [isAdmin]);

  // Milestone trigger — runs whenever fresh data lands
  useEffect(() => {
    if (!data) return;
    const cur = data.current_consecutive_positive ?? 0;
    const threshold = data.gate_threshold_days || 14;
    const seen = readMilestones();
    let mutated = false;
    for (const m of MILESTONES) {
      if (cur >= m && !seen[`m${m}`]) {
        fireMilestoneNotification(m, threshold);
        seen[`m${m}`] = new Date().toISOString();
        mutated = true;
      }
    }
    // Reset if streak fell back below the milestone — so next
    // time it crosses again we re-notify.
    for (const m of MILESTONES) {
      if (cur < m && seen[`m${m}`]) {
        delete seen[`m${m}`];
        mutated = true;
      }
    }
    if (mutated) writeMilestones(seen);
  }, [data]);

  // Visibility gates
  if (!isAuthed) return null;
  if (!isAdmin) return null;
  if (HIDDEN_PATHS.has(pathname)) return null;
  if (err || !data) return null;

  const cur = data.current_consecutive_positive ?? 0;
  const threshold = data.gate_threshold_days || 14;
  const gateOpen = data.enforcement_gate_open === true;
  const rows = data.rows || [];
  // rows are sorted desc by utc_date → rows[0] is today
  const todayPasses = rows[0]?.gate_passes;
  const compact = vpw < 600;

  // Border color reflects trajectory, not just gate state
  let trajectoryColor;
  let trajectoryLabel;
  if (gateOpen) {
    trajectoryColor = C_GREEN;
    trajectoryLabel = 'OPEN';
  } else if (todayPasses === true) {
    trajectoryColor = C_GREEN;
    trajectoryLabel = 'TODAY OK';
  } else if (todayPasses === false) {
    trajectoryColor = C_RED;
    trajectoryLabel = 'TODAY FAIL';
  } else {
    trajectoryColor = C_AMBER;
    trajectoryLabel = 'OBSERVING';
  }

  const Icon = gateOpen ? ShieldCheck : Lock;

  // Environment + evidence-inception context — makes a fresh
  // database (e.g. new production deploy at 0/14) self-explanatory.
  const isPreviewEnv = typeof window !== 'undefined'
    && /preview\.emergentagent\.com|localhost/.test(
      window.location.hostname);
  const envLabel = isPreviewEnv ? 'PREVIEW' : 'PROD';
  let sinceLabel = 'NO EVIDENCE YET';
  if (data.evidence_inception) {
    try {
      const d = new Date(`${data.evidence_inception}T00:00:00Z`);
      sinceLabel = `SINCE ${d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: 'UTC' })
        .toUpperCase()}`;
    } catch {
      sinceLabel = `SINCE ${data.evidence_inception}`;
    }
  }

  // Pulsing dot when streak is non-zero or today is passing but
  // gate hasn't reached threshold yet — early-trajectory signal.
  const showPulse = !gateOpen
    && (todayPasses === true || cur > 0);

  const stateColor = gateOpen ? C_GREEN : C_AMBER;
  const onClick = () => navigate('/bayesian-gate-tracker');

  return (
    <button
      data-testid="bayesian-gate-pill"
      data-gate-state={gateOpen ? 'open' : 'closed'}
      data-today-state={todayPasses === true ? 'pass'
        : todayPasses === false ? 'fail' : 'observing'}
      data-trajectory={trajectoryLabel.toLowerCase()
        .replace(' ', '-')}
      data-compact={compact ? '1' : '0'}
      data-streak={String(cur)}
      data-threshold={String(threshold)}
      data-pulse={showPulse ? '1' : '0'}
      onClick={onClick}
      title={`Bayesian gate · streak ${cur}/${threshold} · `
        + (gateOpen ? 'OPEN' : 'CLOSED')
        + ` · today ${todayPasses === true ? 'PASS'
          : todayPasses === false ? 'FAIL' : '—'}`
        + ' · click to open tracker'}
      style={{
        position: 'fixed',
        top: 'calc(8px + var(--apex-banner-offset, 0px))',
        right: 8,
        maxWidth: 'calc(100vw - 16px)',
        zIndex: 1500,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: compact ? '2px 6px' : '3px 9px',
        background: BG_ELEV,
        border: `1px solid ${trajectoryColor}`,
        borderRadius: 999,
        color: '#e7e7e7',
        fontFamily: MONO,
        fontSize: compact ? 9 : 10,
        letterSpacing: 0.5,
        cursor: 'pointer',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        boxShadow: `0 1px 3px rgba(0,0,0,0.4),
                    0 0 0 1px rgba(0,0,0,0.2)`,
        maxWidth: compact ? 160 : 400,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
      <style>{`
        @keyframes bgp-pulse {
          0%, 100% { opacity: 0.35; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1.1);  }
        }
      `}</style>
      <Icon size={compact ? 10 : 11} color={trajectoryColor} />
      {showPulse && (
        <span data-testid="bgp-pulse"
              style={{
                width: 6, height: 6, borderRadius: 999,
                background: trajectoryColor,
                animation: 'bgp-pulse 1.5s ease-in-out infinite',
                marginRight: 1,
              }} />
      )}
      {compact ? (
        <>
          <span data-testid="bgp-label-compact"
                style={{ color: C_MUTED, fontSize: 8 }}>
            GATE
          </span>
          <span data-testid="bgp-state-compact"
                style={{ color: stateColor, fontWeight: 700 }}>
            {gateOpen ? 'OPEN' : 'CLOSED'}
          </span>
          <span data-testid="bgp-streak-compact"
                style={{ color: trajectoryColor,
                         fontWeight: 700 }}>
            {cur}/{threshold}
          </span>
        </>
      ) : (
        <>
          <span data-testid="bgp-label"
                style={{ color: C_MUTED, fontSize: 8,
                         letterSpacing: 1 }}>
            BAYESIAN GATE
          </span>
          <span data-testid="bgp-state"
                style={{ color: stateColor, fontWeight: 700 }}>
            {gateOpen ? 'OPEN' : 'CLOSED'}
          </span>
          <span data-testid="bgp-streak"
                style={{ color: trajectoryColor,
                         fontWeight: 700 }}>
            {cur}/{threshold}
          </span>
          <span data-testid="bgp-trajectory"
                style={{ color: trajectoryColor,
                         fontSize: 8, opacity: 0.85,
                         marginLeft: 2 }}>
            · {trajectoryLabel}
          </span>
          <span data-testid="bgp-env-inception"
                style={{ color: C_MUTED,
                         fontSize: 8, opacity: 0.9,
                         marginLeft: 2 }}>
            · {envLabel} · {sinceLabel}
          </span>
        </>
      )}
    </button>
  );
}
